import React from "react";
import styled from "styled-components";
import { gradients } from "../theme/gradients";
import { shadows } from "../theme/shadows";
import { colors } from "../theme/colors";

const Card = ({
  children,
  variant = "default",
  hoverable = false,
  animated = false,
  visible = true,
  delay = 0,
  onClick,
  className,
  ...props
}) => {
  const CardComponent = animated ? AnimatedCard : StyledCard;

  return (
    <CardComponent
      variant={variant}
      hoverable={hoverable || !!onClick}
      visible={visible}
      delay={delay}
      onClick={onClick}
      className={className}
      {...props}
    >
      {children}
    </CardComponent>
  );
};

const baseCardStyles = `
  border-radius: 25px;
  padding: 16px 20px;
  position: relative;
  overflow: hidden;
  will-change: transform;
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 80px;
  box-shadow: ${shadows.md};
  border: 1px solid;
`;

const StyledCard = styled.div`
  ${baseCardStyles}

  ${({ variant }) => {
    switch (variant) {
      case "glass":
        return `
          background: ${gradients.glass};
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-color: rgba(255, 255, 255, 0.2);

          &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 1px;
            background: linear-gradient(
              90deg,
              transparent,
              rgba(255, 255, 255, 0.4),
              transparent
            );
          }
        `;
      case "deactivated":
        return `
          background: ${gradients.error};
          border-color: ${colors.error.border};
          opacity: 0.8;
        `;
      default:
        return `
          background: white;
          border-color: ${colors.neutral[200]};
        `;
    }
  }}

  ${({ hoverable }) =>
    hoverable &&
    `
    cursor: pointer;
    transition:
      transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
      box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
      background 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: translateY(-2px);
      box-shadow: ${shadows.lg};

      ${({ variant }) => {
        if (variant === "glass") {
          return `
            border-color: rgba(255, 255, 255, 0.3);
            background: ${gradients.glassStrong};
          `;
        } else if (variant === "deactivated") {
          return `
            opacity: 1;
            background: linear-gradient(
              135deg,
              rgba(231, 76, 60, 0.2) 0%,
              rgba(192, 57, 43, 0.15) 100%
            );
            border-color: rgba(231, 76, 60, 0.3);
          `;
        } else {
          return `
            border-color: ${colors.primary.light};
          `;
        }
      }}
    }
  `}
`;

const AnimatedCard = styled(StyledCard)`
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transform: ${(props) =>
    props.visible ? "translateY(0)" : "translateY(15px)"};
  transition:
    opacity 0.4s ease-out ${(props) => props.delay}ms,
    transform 0.4s ease-out ${(props) => props.delay}ms,
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity, transform;

  &:hover {
    transform: ${(props) =>
      props.visible && props.hoverable
        ? "translateY(-2px)"
        : props.visible
          ? "translateY(0)"
          : "translateY(15px)"};
  }

  ${({ variant }) =>
    variant === "deactivated" &&
    `
    opacity: ${(props) => (props.visible ? 0.8 : 0)};

    &:hover {
      opacity: ${(props) => (props.visible ? 1 : 0)};
    }
  `}
`;

export default Card;
